/* ------------------------------------------------------------------------ */
/*		File: cmpCampaign.tsx                                               */
/*		Description: Component Implementation                               */
/*		Project: iSymWebSchool											    */
/*		Author: ISym Global Solutions Limited                               */
/*		Version: 1.0													    */
/* ------------------------------------------------------------------------ */

import React, { useState } from "react";
import { ICampaign, ICampaignForm } from "./cmpCampaign.d";
import { getImage } from "../../components/GenericComponents/functions";
import "./cmpCampaign.css"
import Button from "../GenericComponents/Button";
import { useTranslation, withTranslation } from "react-i18next";
import Spinner from "../GenericComponents/spinner";
import { subscribeCampaign } from "../../api/hooks";
import { ToastContainer, Zoom, toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";

type FormData = {
    [key: string]: any;
};

/* ------------------------------------------------------------------------ */

const Campaign: React.FC<ICampaign> = ({ campaign, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const infoCampaigns: any = t("campaigns", { returnObjects: true });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<FormData>(() => {
        const fields: ICampaignForm[] = [...infoCampaigns.content[0].content];
        const initialFormData = fields.reduce<FormData>((acc, field) => {
            acc[field.id] = "";
            return acc;
        }, {});
        return initialFormData;
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    /* ---------------------------------------- */
    /* Handler Event: handleChange              */
    /* ---------------------------------------- */

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    /* ---------------------------------------- */
    /* Handler Event: validateField             */
    /* ---------------------------------------- */

    const validateField = (field: ICampaignForm): string | null => {
        const value = formData[field.id];

        if (field.validation?.required) {
            if (!value || value === "") {
                return field.validation.errorMessage || `${field.label} is required.`;
            }
        }

        if (field.validation?.pattern && value) {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(value)) {
                return field.validation.errorMessage || `${field.label} is not valid.`;
            }
        }

        return null;
    };

    /* ---------------------------------------- */
    /* Handler Event: handleCampaign            */
    /* ---------------------------------------- */

    const handleCampaign = async (e: React.FormEvent<HTMLFormElement>) => {
        setIsLoading(true);
        e.preventDefault();

        const newErrors: { [key: string]: string } = {};

        infoCampaigns.content[0].content.forEach((item: ICampaignForm) => {
            const error = validateField(item);
            if (error) { newErrors[item.id] = error; }
        });
        infoCampaigns.content[0].content.forEach((item: ICampaignForm) => {
            const error = validateField(item);
            if (error) { newErrors[item.id] = error; }
        });

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            const payload = {
                ...formData,
                promocodeId: campaign.id,
                promoName: campaign.name,
            }

            const response: any = await subscribeCampaign(payload);
            if (response.msg === "OK") {
                onClose(true);
                navigate(`${infoCampaigns.content[0].buttons[0]?.navigateTo}`)
            } else {
                toast.error(response.msg)
            }
        }
        setIsLoading(false);
    }

    /* ---------------------------------------- */

    return (
        <>
            {isLoading
                ? (<Spinner isLoading={isLoading} type="container" />)
                : (
                    <div className="card_campaign">
                        <div className="card_campaign_content scrollbar-thin scrollbar-thumb-rounded">
                            <div className="card_campaign_title">{campaign.name}</div>
                            <div className="card_campaign_description">{campaign.description}</div>
                            {campaign.src && (
                                <img className="card_campaign_image" src={getImage(campaign.src)} alt={campaign.src} />
                            )}
                            <div className="card_campaign_label">{infoCampaigns.content[0].label}</div>
                            <section id="form">
                                <form id="campaign-form" onSubmit={handleCampaign}>
                                    {infoCampaigns.content[0].content.map((item: any, index: number) => (
                                        <div
                                            key={`div-${index}`}
                                            className={`card_formApply_container ${item.type?.toLowerCase() === "textarea" ? "card_top" : "card_center"
                                                }`}
                                        >
                                            <div className="card_formApply_label">
                                                {item.label}
                                                {item.validation?.required && <span>*</span>}
                                            </div>
                                            <div className="card_formApply_field">
                                                {item.type?.toLowerCase() === "input" && (
                                                    <input
                                                        id={item.id}
                                                        name={item.id}
                                                        type="text"
                                                        value={formData[item.id] as string}
                                                        onChange={handleChange}
                                                    />
                                                )}
                                                {errors[item.id] && <span className="error">{errors[item.id]}</span>}
                                            </div>
                                        </div>
                                    ))}
                                    <div className="card_formApply_button">
                                        <Button
                                            classProp={`${infoCampaigns.content[0].buttons[0].color}-button`}
                                            label={infoCampaigns.content[0].buttons[0].label}
                                            hasIcon={!!infoCampaigns.content[0].buttons[0].src}
                                            src={infoCampaigns.content[0].buttons[0].src}
                                            size={"medium"}
                                        />
                                    </div>
                                </form>
                            </section>
                        </div>

                        <ToastContainer
                            style={{ width: "500pt" }}
                            position="top-center"
                            autoClose={5000}
                            hideProgressBar={false}
                            newestOnTop={false}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                            theme="light"
                            transition={Zoom}
                        />
                    </div>
                )
            }
        </>

    );
};

export default withTranslation('translation', { withRef: true })(Campaign);

